import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ActionIcon,
  Box,
  Group,
  Popover,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import type { DateValue } from "@mantine/dates";
import {
  IconCalendar,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconX,
} from "@tabler/icons-react";
import moment from "moment";
import styles from "./StandardizedDatePicker.module.css";

/** Wire date format shared with the rest of the app / API (`YYYY-MM-DD`). */
const DATE_FORMAT = "YYYY-MM-DD";
const MONTH_FORMAT = "YYYY-MM";

export interface StandardizedDatePickerProps {
  /** Selected date as `YYYY-MM-DD`, or null when unset. */
  value: string | null;
  onChange: (value: string | null) => void;
  /**
   * Anchor the left-rail week offsets measure against (its "now" row = 0).
   * Booking → today; rebook/reschedule → the source appointment's date.
   * Defaults to today.
   */
  baseDate?: string;
  /** Earliest selectable date (`YYYY-MM-DD`). Defaults to today unless `allowPast`. */
  minDate?: string;
  /**
   * Cap for selectable dates, in whole weeks past `baseDate`. Default 12.
   * `null` = no cap.
   */
  maxWeeksAhead?: number | null;
  /** Allow selecting dates before today (drops the default `minDate`). */
  allowPast?: boolean;
  label?: string;
  /**
   * `"inline"` (default) renders the month grid directly. `"trigger"` renders a
   * compact bordered button (calendar icon + formatted value + chevron) that
   * opens the same grid inside a popover. Both variants are generic — no
   * consumer-specific coupling.
   */
  variant?: "inline" | "trigger";
  /** Placeholder shown on the `"trigger"` button when no date is selected. */
  placeholder?: string;
  required?: boolean;
  clearable?: boolean;
  disabled?: boolean;
  /**
   * Dates (`YYYY-MM-DD`) that render a small teal availability dot. Default:
   * no dots.
   */
  availableDates?: string[];
  /**
   * @deprecated The +/- week counter was replaced by the inline month grid;
   * accepted for backwards compatibility but no longer rendered.
   */
  showWeekCounter?: boolean;
  ariaLabel?: string;
  /** Trigger variant: render the button with a red (error) border. */
  error?: boolean;
}

const today = (): string => moment().format(DATE_FORMAT);

const monthOf = (date: string): string =>
  moment(date, DATE_FORMAT).format(MONTH_FORMAT);

/** Anchor date (`YYYY-MM-DD`) for a visible month string (`YYYY-MM`). */
const anchorOf = (month: string): string => `${month}-01`;

interface WeekRow {
  rel: string;
  isNow: boolean;
}

/**
 * Standardized inline date picker — the app's standard Mantine month grid
 * (via `DatePicker` from `@mantine/dates`, so it inherits the shared calendar
 * look: weekday header, cell sizing, disabled styling) plus a left rail of
 * whole-week offsets ("now", "+1w", "-1w", …) measured from `baseDate`. The
 * shared date primitive for the booking / reschedule / rebook flows.
 *
 * The month chevrons move the *visible* month only; they never change the
 * selection. Clicking a day calls `onChange` with its `YYYY-MM-DD`. Days before
 * `minDate` (or before today when `!allowPast`) and days beyond `maxWeeksAhead`
 * from `baseDate` (unless it is `null`) are disabled and hatched.
 */
export default function StandardizedDatePicker({
  value,
  onChange,
  baseDate,
  minDate,
  maxWeeksAhead = 12,
  allowPast = false,
  label,
  variant = "inline",
  placeholder,
  required = false,
  clearable = false,
  disabled = false,
  availableDates,
  ariaLabel,
  error = false,
}: StandardizedDatePickerProps) {
  // Popover open state (only used by the `"trigger"` variant).
  const [popoverOpened, setPopoverOpened] = useState(false);
  // Calendar drill level: "month" = day grid (default), "year" = pick a month,
  // "decade" = pick a year. Driven by the centered label + chevrons below.
  const [level, setLevel] = useState<"month" | "year" | "decade">("month");
  const base = baseDate ?? today();
  const min = minDate ?? (allowPast ? undefined : today());
  const max =
    maxWeeksAhead === null
      ? undefined
      : moment(base, DATE_FORMAT)
          .add(maxWeeksAhead, "weeks")
          .format(DATE_FORMAT);

  // Visible month anchor (`YYYY-MM-DD`, controls Mantine's displayed month);
  // seeds from the selection, else the anchor/today.
  const [visibleDate, setVisibleDate] = useState<string>(() =>
    anchorOf(monthOf(value ?? base)),
  );

  // Follow the selection into its month when it changes (external update or a
  // trailing/leading-day pick).
  useEffect(() => {
    if (value) setVisibleDate(anchorOf(monthOf(value)));
  }, [value]);

  const dotSet = useMemo(() => new Set(availableDates ?? []), [availableDates]);

  const isBlocked = useCallback(
    (date: string): boolean =>
      (min !== undefined && date < min) || (max !== undefined && date > max),
    [min, max],
  );

  // Left-rail rows for the visible month: one label per Mantine week row
  // (start-of-week Sunday … through the last week the month touches).
  const weeks = useMemo<WeekRow[]>(() => {
    const first = moment(visibleDate, DATE_FORMAT).startOf("month");
    const gridStart = first.clone().startOf("week");
    const gridEnd = first.clone().endOf("month").endOf("week");
    const numWeeks = gridEnd.diff(gridStart, "weeks") + 1;
    const baseWeekStart = moment(base, DATE_FORMAT).startOf("week");

    const rows: WeekRow[] = [];
    for (let r = 0; r < numWeeks; r++) {
      const rowStart = gridStart.clone().add(r, "weeks");
      const diff = Math.round(rowStart.diff(baseWeekStart, "days") / 7);
      rows.push({
        rel: diff === 0 ? "now" : `${diff > 0 ? "+" : ""}${diff}w`,
        isNow: diff === 0,
      });
    }
    return rows;
  }, [visibleDate, base]);

  // ---- Rail ↔ calendar row alignment -------------------------------------
  // Mantine renders the month as a <table>; measure the first week row's
  // vertical offset (past the nav header + weekday row) and its height, then
  // lay the rail out to match row-for-row. Measured in a layout effect so it
  // lands before paint (no flash), and re-measured on resize.
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const railRef = useRef<HTMLDivElement | null>(null);
  const [rail, setRail] = useState<{ top: number; rowH: number } | null>(null);

  // Returns true once it has a valid measurement (the Mantine <table> exists and
  // has laid out). In "trigger" mode the popover mounts the table asynchronously
  // (after a transition), so a single layout-effect pass often measures nothing —
  // the retry loop below re-runs until rows exist.
  const measure = useCallback(() => {
    const wrap = wrapRef.current;
    const railEl = railRef.current;
    if (!wrap || !railEl) return false;
    const rows = wrap.querySelectorAll("table tbody tr");
    if (rows.length === 0) return false;
    const railTop = railEl.getBoundingClientRect().top;
    const firstRow = (rows[0] as HTMLElement).getBoundingClientRect();
    if (firstRow.height === 0) return false;
    const top = firstRow.top - railTop;
    const rowH = firstRow.height;
    setRail((prev) =>
      prev && prev.top === top && prev.rowH === rowH ? prev : { top, rowH },
    );
    return true;
  }, []);

  // Measure with a rAF retry loop so the rail aligns even when the calendar
  // mounts late (inside the trigger popover). Also re-measure on resize.
  useLayoutEffect(() => {
    let raf = 0;
    let tries = 0;
    const tick = () => {
      const ok = measure();
      tries += 1;
      // Keep retrying for ~30 frames (covers the popover open transition), and
      // one extra pass after success in case the transition shifts things.
      if (tries < 30 && (!ok || tries < 3)) {
        raf = requestAnimationFrame(tick);
      }
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [measure, visibleDate, popoverOpened, disabled, weeks.length, level]);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(wrap);
    const table = wrap.querySelector("table");
    if (table) ro.observe(table);
    return () => ro.disconnect();
  }, [measure, popoverOpened, visibleDate]);

  // In "trigger" mode, picking a day (non-null) closes the popover; clearing
  // (null) leaves it open. "inline" mode passes the caller's onChange through.
  const gridOnChange = (next: string | null) => {
    onChange(next);
    if (variant === "trigger" && next !== null) setPopoverOpened(false);
  };

  const handlePick = (v: DateValue) => {
    const next =
      v == null
        ? null
        : typeof v === "string"
          ? v
          : moment(v).format(DATE_FORMAT);
    gridOnChange(next);
  };

  // Custom full-width header (Mantine's built-in header is hidden) so the label
  // centers over the whole picker — rail + grid — with chevrons at the far
  // edges. The chevrons move the visible period by the current level's unit
  // (month / year / decade); they never touch the selection.
  const shiftPeriod = (dir: number) => {
    const unit = level === "month" ? "month" : "year";
    const amount = level === "decade" ? dir * 10 : dir;
    setVisibleDate(
      moment(visibleDate, DATE_FORMAT).add(amount, unit).format(DATE_FORMAT),
    );
  };

  // Clicking the label drills up a level (month → year → decade).
  const drillUp = () => setLevel((l) => (l === "month" ? "year" : "decade"));

  const levelLabel = (): string => {
    const m = moment(visibleDate, DATE_FORMAT);
    if (level === "year") return m.format("YYYY");
    if (level === "decade") {
      const start = Math.floor(m.year() / 10) * 10;
      return `${start} – ${start + 9}`;
    }
    return m.format("MMMM YYYY");
  };

  const calendar = (
    <Box
      className={styles.calendarBlock}
      role="group"
      aria-label={ariaLabel ?? label ?? "Date picker"}
    >
      {/* Full-width month header spanning rail + grid: chevrons at the far-left
          and far-right edges, month/year centered between them. */}
      <Box
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
          marginBottom: 6,
        }}
      >
        <ActionIcon
          variant="subtle"
          color="gray"
          size="lg"
          radius="md"
          onClick={() => shiftPeriod(-1)}
          aria-label="Previous"
          style={{ justifySelf: "start" }}
        >
          <IconChevronLeft size={20} />
        </ActionIcon>
        <UnstyledButton
          onClick={drillUp}
          aria-label="Change month and year"
          style={{ justifySelf: "center" }}
        >
          <Text fw={600} size="md" ta="center">
            {levelLabel()}
          </Text>
        </UnstyledButton>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="lg"
          radius="md"
          onClick={() => shiftPeriod(1)}
          aria-label="Next"
          style={{ justifySelf: "end" }}
        >
          <IconChevronRight size={20} />
        </ActionIcon>
      </Box>

      <Box
        ref={wrapRef}
        className={styles.calendarWrap}
        data-disabled={disabled ? "true" : undefined}
      >
        {level === "month" && (
          <Box ref={railRef} className={styles.rail}>
            {weeks.map((row, r) => (
              <Text
                key={r}
                className={styles.railLabel}
                data-now={row.isNow ? "true" : undefined}
                style={
                  rail
                    ? {
                        height: rail.rowH,
                        marginTop: r === 0 ? rail.top : undefined,
                      }
                    : undefined
                }
                span
              >
                {row.rel}
              </Text>
            ))}
          </Box>
        )}

        <DatePicker
          className={styles.picker}
          styles={{ calendarHeader: { display: "none" } }}
          date={visibleDate}
          onDateChange={setVisibleDate}
          level={level}
          onLevelChange={setLevel}
          value={value}
          onChange={handlePick}
          size="lg"
          firstDayOfWeek={0}
          weekendDays={[]}
          excludeDate={isBlocked}
          highlightToday
          hideOutsideDates={false}
          nextLabel="Next month"
          previousLabel="Previous month"
          weekdayFormat={(d) =>
            moment(d, DATE_FORMAT).format("dd").toUpperCase()
          }
          getDayAriaLabel={(d) => moment(d, DATE_FORMAT).format("MMMM D, YYYY")}
          getDayProps={(d) => {
            const classes = [
              isBlocked(d) ? styles.blockedDay : "",
              // Shade every Sunday (day-of-week 0), matching the design.
              moment(d, DATE_FORMAT).day() === 0 ? styles.sundayDay : "",
            ]
              .filter(Boolean)
              .join(" ");
            return {
              "aria-pressed": value !== null && d === value,
              className: classes || undefined,
            };
          }}
          renderDay={(d) => {
            const m = moment(d, DATE_FORMAT);
            const hasDot = dotSet.has(d) && d !== value;
            return (
              <Box className={styles.dayInner}>
                <Text span>{m.date()}</Text>
                {hasDot && <Box className={styles.dot} />}
              </Box>
            );
          }}
        />
      </Box>

      {clearable && value !== null && !disabled && (
        <Group justify="flex-end" gap={0} mt={4}>
          <UnstyledButton
            className={styles.clearButton}
            onClick={() => gridOnChange(null)}
            aria-label="Clear date"
          >
            <IconX size={13} />
            <Text span className={styles.clearLabel}>
              Clear
            </Text>
          </UnstyledButton>
        </Group>
      )}
    </Box>
  );

  return (
    <Box className={styles.root}>
      {label && (
        <Text component="label" className={styles.label}>
          {label}
          {required && (
            <Text span className={styles.required}>
              *
            </Text>
          )}
        </Text>
      )}
      {variant === "trigger" ? (
        <Popover
          opened={popoverOpened}
          onChange={setPopoverOpened}
          position="bottom-start"
          shadow="md"
          radius={10}
          withinPortal
          disabled={disabled}
        >
          <Popover.Target>
            <UnstyledButton
              className={styles.trigger}
              onClick={() => !disabled && setPopoverOpened((o) => !o)}
              disabled={disabled}
              aria-label={ariaLabel ?? label ?? "Select date"}
              style={
                error
                  ? {
                      borderColor: "var(--mantine-color-red-6)",
                      color: "var(--mantine-color-red-6)",
                    }
                  : undefined
              }
            >
              <IconCalendar size={16} className={styles.triggerIcon} />
              <Text
                span
                className={`${styles.triggerValue} ${
                  value ? "" : styles.triggerPlaceholder
                }`}
              >
                {value
                  ? moment(value, DATE_FORMAT).format("ddd, MMM D")
                  : (placeholder ?? "Select date")}
              </Text>
              <IconChevronDown size={16} className={styles.triggerChevron} />
            </UnstyledButton>
          </Popover.Target>
          <Popover.Dropdown p={6}>{calendar}</Popover.Dropdown>
        </Popover>
      ) : (
        calendar
      )}
    </Box>
  );
}
